import { getStytchCookieDomainForHostname } from '@/utilities/getDomain'
import {
  STYTCH_SESSION_JWT_COOKIE,
  STYTCH_SESSION_OPAQUE_COOKIE,
} from '@/utilities/stytch/constants'

function getServerCookieDomain(host: string | null | undefined): string | undefined {
  if (!host) return undefined
  try {
    const domain = getStytchCookieDomainForHostname(new URL(`http://${host}`).hostname)
    return domain === 'localhost' ? undefined : domain
  } catch {
    return undefined
  }
}

/**
 * The Stytch SDK writes the session cookies with `Domain=<tenant hostname>` and no Domain on
 * localhost. Server writes must use the same Domain, or the browser keeps a second cookie with
 * the same name. The gateway then authenticates with whichever cookie comes first, which can be
 * a different member than the one the SDK holds.
 */
export function getStytchSessionCookieOptions(host: string | null | undefined) {
  const domain = getServerCookieDomain(host)
  return {
    httpOnly: false,
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'lax' as const,
    path: '/',
    ...(domain ? { domain } : {}),
  }
}

/**
 * `Set-Cookie` values that expire `name` in the host-only form and, when `domain` is set, in the
 * `Domain=<domain>` form. A cookie is removed only when the Domain matches, and both forms can
 * exist.
 */
export function getExpiredStytchCookies(name: string, domain?: string): string[] {
  const secure = process.env.NODE_ENV === 'production' ? '; Secure' : ''
  const expired = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; SameSite=Lax${secure}`
  return domain ? [expired, `${expired}; Domain=${domain}`] : [expired]
}

/**
 * Ends the Stytch session from the server. If the opaque token stays, the SDK makes a new JWT on
 * the next page load, so both cookies must expire.
 */
export function expireStytchSessionCookies(
  responseHeaders: Headers,
  host: string | null | undefined,
) {
  const domain = getServerCookieDomain(host)
  for (const name of [STYTCH_SESSION_JWT_COOKIE, STYTCH_SESSION_OPAQUE_COOKIE]) {
    for (const cookie of getExpiredStytchCookies(name, domain)) {
      responseHeaders.append('Set-Cookie', cookie)
    }
  }
}
